import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { api, ApiError, type OrgUser, type Role } from '../lib/api'
import { useDialogGuard, useReturnFocus } from '../lib/sheet'

/** The organization's own logins — `PLAN.html` sections 19 and 20.
 *
 *  ONLY DISPATCHERS LOG IN. Members never do: they are reached by radio, card,
 *  text and email and have no account here. So a user IS a dispatcher seat,
 *  and this is where the company's own administrator makes them.
 *
 *  THE FIRST ONE IS STILL MADE OUTSIDE, in the super admin console, because
 *  somebody has to exist before anybody can sign in and create anybody. Every
 *  one after that is made here — the customer's words: "administrator of the
 *  company could add as many users he want under his company".
 *
 *  A ROLE IS PART OF CREATING A USER, not a step afterwards. A login with no
 *  role can sign in and is then refused by every button, permanently, with
 *  nothing to distinguish that from a broken screen — which is the exact state
 *  the role editor was built to end.
 *
 *  AND NOBODY IS DELETED. Disabling ends the session at the next click —
 *  `disabled` is read on every request — and keeps every "who did this"
 *  reference pointing at a real row.
 */

const BLANK = {
  login: '',
  display_name: '',
  email: '',
  phone: '',
  password: '',
  role_id: '',
}

export function Users() {
  const [users, setUsers] = useState<OrgUser[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loaded, setLoaded] = useState(false)
  const [adding, setAdding] = useState(false)
  const [record, setRecord] = useState<OrgUser | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [u, r] = await Promise.all([api.users(), api.roles()])
      setUsers(u)
      setRoles(r.roles)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the users.')
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => { load() }, [load])

  function absorb(updated: OrgUser) {
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)))
    setRecord((open) => (open && open.id === updated.id ? updated : open))
  }

  const live = users.filter((u) => !u.disabled).length

  return (
    <>
      {error && (
        <div className="notice" role="alert" onClick={() => setError(null)}>{error}</div>
      )}
      {note && (
        <div className="notice ok" role="status" onClick={() => setNote(null)}>{note}</div>
      )}

      <div className="sheetbar flat">
        <h3 className="sub">Users</h3>
        <span className="count">{live}</span>
        <span className="spacer" />
        <button className="primary compact" onClick={() => setAdding(true)}>
          + Add user
        </button>
      </div>

      <p className="blurb">
        One login per dispatcher. Members never sign in — they are reached by
        radio, card, text and email. The very first login was made outside the
        CRM; every one after it is made here.
      </p>

      <table className="maptable">
        <thead>
          <tr>
            <th>Name</th><th>Login</th><th>Role</th><th>Panel</th>
            <th>Last seen</th><th />
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr>
              <td colSpan={6} className="sheetempty">
                {loaded ? 'No logins yet.' : 'Loading…'}
              </td>
            </tr>
          ) : users.map((u) => (
            <tr key={u.id} className={u.disabled ? 'retiredrow' : ''}>
              <td className="nm">
                <span className={u.disabled ? 'gone' : ''}>{u.display_name}</span>
              </td>
              <td className="mono">{u.login}</td>
              <td>
                {u.role_name
                  ? <span className="lbl">{u.role_name}</span>
                  : <span className="unreadable">no role</span>}
              </td>
              <td>
                {/* PENDING rather than a promise. The WalkieFleet account
                    cannot be made by any API -- it is created by hand on the
                    fleet server -- so a dispatcher without one must be shown
                    as waiting rather than framed onto nothing. */}
                {u.fleet_account_ready
                  ? <span className="muted">ready</span>
                  : <span className="src">pending radio account</span>}
              </td>
              <td className="muted when">
                {u.last_seen_at
                  ? new Date(u.last_seen_at).toLocaleDateString(
                    [], { day: '2-digit', month: 'short' })
                  : 'never'}
              </td>
              <td>
                <button
                  className="ghost tiny nogrow"
                  type="button"
                  onClick={() => setRecord(u)}
                >
                  Open
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {adding && (
        <AddUser
          roles={roles}
          onClose={() => setAdding(false)}
          onAdded={(made) => {
            setAdding(false)
            setNote(`${made.display_name} can sign in as “${made.login}”. `
              + 'Give them the password you just set — it is not stored '
              + 'anywhere it can be read back.')
            load()
          }}
        />
      )}

      {record && (
        <EditUser
          key={record.id}
          user={record}
          roles={roles}
          onClose={() => setRecord(null)}
          onSaved={(updated, message) => {
            absorb(updated)
            setRecord(null)
            if (message) setNote(message)
          }}
        />
      )}
    </>
  )
}

function RoleField({
  roles, value, onChange, id,
}: {
  roles: Role[]
  value: string
  onChange: (id: string) => void
  id: string
}) {
  return (
    <div className="field">
      <label htmlFor={id}>Role</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} required>
        <option value="">Choose a role…</option>
        {roles.map((r) => (
          <option key={r.id} value={r.id}>{r.name}</option>
        ))}
      </select>
      <p className="footnote">
        What this person can reach. A login with no role can sign in and is
        then refused by every button, so one is required.
      </p>
    </div>
  )
}

function AddUser({
  roles, onClose, onAdded,
}: {
  roles: Role[]
  onClose: () => void
  onAdded: (made: OrgUser) => void
}) {
  const [form, setForm] = useState({ ...BLANK })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { leave, backdrop } = useDialogGuard(
    () => (JSON.stringify(form) !== JSON.stringify(BLANK)
      ? 'Discard this user? Nothing has been saved yet.'
      : null),
    onClose,
  )

  const short = form.password.length > 0 && form.password.length < 12

  async function add(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onAdded(await api.userCreate({
        login: form.login.trim(),
        display_name: form.display_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        password: form.password,
        role_id: form.role_id,
      }))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add that user.')
      setBusy(false)
    }
  }

  return (
    <div className="modalback" {...backdrop}>
      <form
        className="modal wide sheet-rec"
        role="dialog"
        aria-modal="true"
        aria-labelledby="adduser"
        onSubmit={add}
      >
        <header className="sheet-rechead">
          <h3 id="adduser">Add a user</h3>
          <span className="spacer" />
          <button className="sheet-recx" type="button" aria-label="Close" onClick={leave}>
            ×
          </button>
        </header>

        <div className="sheet-recbody">
          {error && <div className="notice" role="alert">{error}</div>}

          <div className="grid2">
            <div className="field">
              <label htmlFor="nu-name">Name</label>
              <input
                id="nu-name"
                value={form.display_name}
                autoFocus
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="nu-login">Login</label>
              <input
                id="nu-login"
                value={form.login}
                autoComplete="off"
                onChange={(e) => setForm({ ...form, login: e.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="nu-email">Email</label>
              <input
                id="nu-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="nu-phone">Phone</label>
              <input
                id="nu-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </div>

          <RoleField
            roles={roles}
            value={form.role_id}
            id="nu-role"
            onChange={(role_id) => setForm({ ...form, role_id })}
          />

          <div className="field">
            <label htmlFor="nu-pw">Password — at least 12 characters</label>
            <input
              id="nu-pw"
              type="password"
              value={form.password}
              autoComplete="new-password"
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
            />
            {short && (
              <p className="footnote warn">
                Twelve characters at least. A long one you can say out loud
                beats a short one with a symbol in it.
              </p>
            )}
          </div>
          {/* Said before they type it, not after. There is no reset-by-email
              in this product yet, so a forgotten password is a job for whoever
              holds this screen. */}
          <p className="footnote">
            Write it down and give it to them. It is hashed the moment it
            arrives and is not stored anywhere it can be read back — if it is
            lost, somebody with this screen sets a new one.
          </p>
        </div>

        <div className="modalrow">
          <button className="ghost" type="button" onClick={leave}>Cancel</button>
          <button
            className="primary compact"
            disabled={busy || !form.display_name.trim() || !form.login.trim()
              || !form.role_id || form.password.length < 12}
          >
            {busy ? 'Saving…' : 'Create the login'}
          </button>
        </div>
      </form>
    </div>
  )
}

function EditUser({
  user, roles, onClose, onSaved,
}: {
  user: OrgUser
  roles: Role[]
  onClose: () => void
  onSaved: (updated: OrgUser, message?: string) => void
}) {
  const [form, setForm] = useState({
    display_name: user.display_name,
    email: user.email ?? '',
    phone: user.phone ?? '',
    role_id: user.role_id ?? '',
  })
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const initial = JSON.stringify({
    display_name: user.display_name,
    email: user.email ?? '',
    phone: user.phone ?? '',
    role_id: user.role_id ?? '',
  })

  useReturnFocus()

  const dirty = JSON.stringify(form) !== initial || password.length > 0
  const { leave, backdrop } = useDialogGuard(
    () => (dirty ? `Discard your unsaved changes to ${user.display_name}?` : null),
    onClose,
  )

  async function save(e: FormEvent) {
    e.preventDefault()
    if (password && password.length < 12) {
      setError('A password needs at least 12 characters.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        display_name: form.display_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        role_id: form.role_id,
      }
      if (password) body.password = password
      onSaved(await api.userUpdate(user.id, body),
        password ? `A new password is set for ${user.display_name}.` : undefined)
    } catch (err) {
      // The server's own sentence -- the lock-out refusal in particular, which
      // explains that this is the screen that would put it back.
      setError(err instanceof ApiError ? err.message : 'Could not save that.')
      setBusy(false)
    }
  }

  async function toggleDisabled() {
    const off = !user.disabled
    if (!confirm(off
      ? `Switch off ${user.display_name}'s login? They are signed out at the `
        + 'next thing they click, and everything they have done stays on record.'
      : `Switch ${user.display_name}'s login back on?`)) return
    setBusy(true)
    setError(null)
    try {
      onSaved(await api.userUpdate(user.id, { disabled: off }),
        off ? `${user.display_name} can no longer sign in.`
          : `${user.display_name} can sign in again.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that.')
      setBusy(false)
    }
  }

  return (
    <div className="modalback" {...backdrop}>
      <form
        className="modal wide sheet-rec"
        role="dialog"
        aria-modal="true"
        aria-labelledby="eduser"
        onSubmit={save}
      >
        <header className="sheet-rechead">
          <h3 id="eduser">{user.display_name}</h3>
          <span className={dirty ? 'sheet-recstate dirty' : 'sheet-recstate'}>
            {dirty ? 'Unsaved changes' : 'Editing'}
          </span>
          <span className="spacer" />
          <button className="sheet-recx" type="button" aria-label="Close" onClick={leave}>
            ×
          </button>
        </header>

        <div className="sheet-recbody">
          {error && <div className="notice" role="alert">{error}</div>}
          {user.disabled && (
            <div className="notice wait" role="status">
              This login is switched off. They cannot sign in.
            </div>
          )}
          {!user.fleet_account_ready && (
            <div className="notice" role="status">
              Waiting on a radio account. The WalkieFleet account cannot be
              created from here — it is made by hand on the fleet server. Until
              it exists this dispatcher sees the panel as pending rather than a
              screen that does nothing.
            </div>
          )}

          <div className="grid2">
            <div className="field">
              <label htmlFor="eu-name">Name</label>
              <input
                id="eu-name"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="eu-login">Login</label>
              {/* Read-only on purpose. A login is what a session, an audit
                  trail and a person's own muscle memory point at; renaming it
                  is not an edit, it is a different account. */}
              <input id="eu-login" value={user.login} readOnly />
            </div>
            <div className="field">
              <label htmlFor="eu-email">Email</label>
              <input
                id="eu-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="eu-phone">Phone</label>
              <input
                id="eu-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </div>

          <RoleField
            roles={roles}
            value={form.role_id}
            id="eu-role"
            onChange={(role_id) => setForm({ ...form, role_id })}
          />

          <div className="field">
            <label htmlFor="eu-pw">Set a new password</label>
            <input
              id="eu-pw"
              type="password"
              value={password}
              autoComplete="new-password"
              placeholder="Leave blank to keep the current one"
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="footnote">
              There is no reset-by-email in this product, so a forgotten
              password is set here. The old one is not shown and cannot be.
            </p>
          </div>
        </div>

        <div className="modalrow">
          <button
            className={user.disabled ? 'ghost ok' : 'ghost danger'}
            type="button"
            disabled={busy}
            onClick={toggleDisabled}
          >
            {user.disabled ? 'Switch the login back on' : 'Switch off this login'}
          </button>
          <span className="spacer" />
          <button className="ghost" type="button" onClick={leave}>Cancel</button>
          <button
            className="primary compact"
            disabled={busy || !dirty || !form.display_name.trim() || !form.role_id}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  )
}
